import Vue from 'www/vue_components'
import vuetify from 'www/modules/vuetify-plugin'
import { pinia } from 'www/plugins'
import AppointmentCalendar from 'www/vue_components/common/AppointmentCalendar/AppointmentCalendar.vue'
import { dispatchCustomEventGlobally } from 'www/utils'
import { SCHEDULE_EVENTS, PAGE_TYPE_DATA } from 'www/modules/Schedule/constants'
import { getRequestDataInterface } from 'www/modules/Schedule/functions'
import { scheduleRequest } from 'www/modules/Schedule/api'
import getInitialDay from 'www/modules/Schedule/classes/ScheduleData/functions/renderData/helpers/getInitialDay'
import useInvasiveServiceInfoStore from 'src/components/common/InvasiveServiceInfo/stores/useInvasiveServiceInfoStore'
import InvasiveServiceInfo from 'src/components/common/InvasiveServiceInfo/InvasiveServiceInfo.vue'
import 'www/doctors.blocks/common/b-appointment-calendar-services/b-appointment-calendar-services.scss'

class ScheduleVue {
  #options = {
    namespace: 'schedule-container',
  }

  constructor(opts = {}) {
    this.#options = { ...this.#options, ...opts }

    const handleClickSynonymPrice = this.handleClickSynonymPrice.bind(this)

    window.addEventListener(SCHEDULE_EVENTS.clickSynonymPrice, handleClickSynonymPrice)
  }

  /**
   * @public
   * @description
   *
   * Производит сбор данных для запроса за расписанием и производит рендер расписания.
   * Метод конкретно обрабатывает страницу цен клиники.
   * В случае ошибки или отсутствия слотов - эмитит событие для возможности обработки вне модуля
   * В случае успешного запроса и наличия расписания - производит рендер и также эмитит событие для возможности его обработки вне модуля
   *
   * @param { object } detail -
   * @param { number } detail.lpuId - id клиники
   * @param { number | undefined } detail.doctorId - id клиники
   * @param { { wrapperRowScheduleContainer: HTMLElement } } detail.scheduleElements - данные с узлами на странице(кнопка по которой кликнули, контейнер для монтирования расписания и иные элементы)
   * @param { string } detail.dateStart - дата начала расписания, нужно для запроса
   * @param { number } detail.synonymId - id синонима, нужно для запроса
   * @param { number } detail.townTimedelta - timedelta, нужно для запроса
   * @param { boolean } detail.isEnabledDirect - доступна ли у клиники ОЗ, нужно во время редиректа по слоту, чтобы работал нужный флоу
   * @param { boolean } detail.isEnabledLpuClub - наличие клубного договора 10% у клиники, нужно во время редиректа по слоту, чтобы работал нужный флоу
   * @param { boolean } detail.isPreliminaryConsultation - требуется ли первичная консультация, нужно во время редиректа по слоту, чтобы работал нужный флоу
   * @param { boolean } detail.isLpuPriceList - список цен клиники
   * @param { boolean } detail.isDoctorPriceList - список цен врача
   */

  async handleClickSynonymPrice({ detail }) {
    try {
      const pageType = PAGE_TYPE_DATA.services
      const requestDataInterface = getRequestDataInterface({ pageType })
      const {
        lpuId,
        dateStart,
        doctorId,
        synonymId,
        townTimedelta,
        isEnabledDirect,
        isEnabledLpuClub,
        scheduleElements,
        isLpuPriceList,
        isDoctorPriceList,
        serviceData,
      } = detail

      const hasCalendarContainer = scheduleElements.wrapperRowScheduleContainer.querySelector('.schedule-container-loaded')

      if (hasCalendarContainer) {
        dispatchCustomEventGlobally(SCHEDULE_EVENTS.renderSuccess, {
          scheduleElements,
          synonymId: detail.synonymId,
        })

        return
      }

      requestDataInterface.day_start = dateStart
      requestDataInterface.town_timedelta = townTimedelta

      if (doctorId) {
        requestDataInterface.services.push({
          lpu_id: lpuId,
          doctor_id: doctorId,
          service_id: synonymId,
        })
      } else {
        requestDataInterface.services.push({
          lpu_id: lpuId,
          service_id: synonymId,
        })
      }

      const { result } = await scheduleRequest({
        pageType,
        data: requestDataInterface,
      })

      if (!result?.length) {
        dispatchCustomEventGlobally(SCHEDULE_EVENTS.redirectSynonymPrice, {
          synonymId,
        })

        return
      }

      const { slots } = result[0]
      const days = Object.keys(slots)
      const daysWithSlots = days.filter(date => slots[date].length)
      const isEmptySlotList = !daysWithSlots.length

      if (isEmptySlotList) {
        dispatchCustomEventGlobally(SCHEDULE_EVENTS.redirectSynonymPrice, {
          synonymId,
        })

        return
      }

      const initDay = getInitialDay({
        daysWithSlots,
        currentSlots: slots,
      })

      const props = {
        slots,
        data: slots,
        options: slots,
        days,
        lpuId,
        doctorId,
        dtypeId: 0, // Для страницы списка цен dtypeId в форму передавать необязательно, так как на backend есть механизм для его получения без GET параметра
        initDay,
        synonymId,
        daysWithSlots,
        serviceIsDirect: isEnabledDirect,
        isEnabledLpuClub,
        serviceData,
        useRedirect: true,
        isLpuPriceList,
        isDoctorPriceList,
        isAppointmentLpu: true,
      }

      const { namespace } = this.#options
      const namespaceList = window.MOBILE_VERSION ? `${namespace}-mobile` : `${namespace}-desktop`

      const mobileMargin = isDoctorPriceList ? 'mx-n4 mt-n2' : 'mx-n2 mt-n2'
      const classList = [
        !window.MOBILE_VERSION ? 'b-appointment-calendar-services' : '',
        'schedule-container schedule-container-loaded',
        window.MOBILE_VERSION ? mobileMargin : '',
        namespaceList,
      ].filter(className => className)

      scheduleElements.wrapperRowScheduleContainer.innerHTML = (`
        <div class="${classList.join(' ')}">
          <div class="schedule"></div>
        </div>
      `)

      const invasiveServiceInfoStore = useInvasiveServiceInfoStore(pinia)

      if (
        !invasiveServiceInfoStore.isCreatedComponent
        && (
          serviceData.allowTreatmentReferral
          || (serviceData.requiresPrimaryConsultation && serviceData.hasPrimaryConsultationDoctors)
        )
      ) {
        const mountEl = document.createElement('div')

        scheduleElements.wrapperRowScheduleContainer.append(mountEl)

        new Vue({
          vuetify,
          el: mountEl,
          name: 'InvasiveServiceInfoRoot',
          pinia,
          render: h => h(InvasiveServiceInfo),
        })
      }

      const additionalProps = {
        paddingX: window.MOBILE_VERSION ? 8 : undefined,
      }

      new Vue({
        el: scheduleElements.wrapperRowScheduleContainer.querySelector('.schedule'),
        pinia,
        render: h => h(AppointmentCalendar, {
          props: {
            ...props,
            ...additionalProps,
          },
        }),
      })

      dispatchCustomEventGlobally(SCHEDULE_EVENTS.renderSuccess, {
        scheduleElements,
        synonymId: detail.synonymId,
      })
    } catch {
      const { synonymId } = detail

      dispatchCustomEventGlobally(SCHEDULE_EVENTS.redirectSynonymPrice, {
        synonymId,
      })
    }
  }
}

export default ScheduleVue
